/**
 * Battle-tested 3-layer auth context for Supabase.
 *
 * Strategy:
 *   1. Synchronous localStorage read — instant session restore, no login flash
 *   2. Direct PostgREST REST call — fetch memberships before Supabase JS client init
 *   3. onAuthStateChange — authoritative session, only sets React state (no async Supabase calls)
 *
 * CRITICAL: Never call Supabase queries/getSession/getUser inside onAuthStateChange.
 * It causes deadlocks (auth-js#762) or token-refresh loops (supabase-js#2126).
 *
 * getSession(), setSession(), and INITIAL_SESSION can ALL hang for 30-60+ seconds.
 * This context avoids them entirely.
 */

import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import type { Session, User } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'
import type { Membership, Business } from '@/types/database'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AuthState {
  session: Session | null
  user: User | null
  memberships: (Membership & { business: Business })[]
  /** True while the initial auth check is in progress */
  loading: boolean
  /** True while memberships are being fetched (after auth resolved) */
  membershipsLoading: boolean
  /** True when a stored session couldn't be restored (token expired) */
  sessionExpired: boolean
  signIn: (email: string, password: string) => Promise<{ error: string | null }>
  signOut: () => Promise<void>
  refreshMemberships: () => Promise<void>
}

const AuthContext = createContext<AuthState | undefined>(undefined)

// ---------------------------------------------------------------------------
// Constants — UPDATE THESE FOR YOUR PROJECT
// ---------------------------------------------------------------------------

// Supabase stores its auth token under this key in localStorage.
// Format: sb-{project-ref}-auth-token
const STORAGE_KEY = 'sb-mgqgbzrdhsayclksajrr-auth-token'
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Fetch memberships via direct PostgREST call with an explicit access token.
 * This works even before the Supabase JS client's auth layer initialises.
 */
async function fetchMembershipsREST(
  userId: string,
  accessToken: string,
): Promise<(Membership & { business: Business })[]> {
  const url =
    `${SUPABASE_URL}/rest/v1/memberships` +
    `?select=*,business:businesses(*)` +
    `&user_id=eq.${userId}`

  const res = await fetch(url, {
    headers: {
      apikey: SUPABASE_ANON_KEY,
      Authorization: `Bearer ${accessToken}`,
      Accept: 'application/json',
    },
  })

  if (!res.ok) {
    console.error('[Auth] memberships REST:', res.status)
    return []
  }

  const data = await res.json()
  if (!Array.isArray(data)) return []

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return data.map((m: any) => ({
    id: m.id,
    user_id: m.user_id,
    business_id: m.business_id,
    role: m.role,
    created_at: m.created_at,
    updated_at: m.updated_at,
    business: m.business as Business,
  }))
}

/**
 * Fetch memberships via the Supabase JS client.
 * Only call this OUTSIDE of onAuthStateChange to avoid deadlocks.
 */
async function fetchMemberships(
  userId: string,
): Promise<(Membership & { business: Business })[]> {
  const { data, error } = await supabase
    .from('memberships')
    .select('*, business:businesses(*)')
    .eq('user_id', userId)

  if (error) {
    console.error('[Auth] fetchMemberships:', error.message)
    return []
  }
  if (!data) return []

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return data.map((m: any) => ({
    id: m.id,
    user_id: m.user_id,
    business_id: m.business_id,
    role: m.role,
    created_at: m.created_at,
    updated_at: m.updated_at,
    business: m.business as Business,
  }))
}

/**
 * Synchronously read session + user from localStorage.
 * Returns null if no valid token exists.
 */
function readStoredSession(): { session: Session; user: User } | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const stored = JSON.parse(raw)
    if (!stored.access_token || !stored.refresh_token || !stored.user) return null

    // Reject clearly-expired tokens (60 s buffer)
    if (stored.expires_at) {
      const now = Math.floor(Date.now() / 1000)
      if (stored.expires_at < now - 60) return null
    }

    return {
      session: {
        access_token: stored.access_token,
        refresh_token: stored.refresh_token,
        expires_in: stored.expires_in ?? 3600,
        expires_at: stored.expires_at,
        token_type: stored.token_type ?? 'bearer',
        user: stored.user,
      },
      user: stored.user as User,
    }
  } catch {
    return null
  }
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function AuthProvider({ children }: { children: ReactNode }) {
  // ---- Eager restore (synchronous) ----
  const stored = readStoredSession()

  const [session, setSession] = useState<Session | null>(stored?.session ?? null)
  const [user, setUser] = useState<User | null>(stored?.user ?? null)
  const [memberships, setMemberships] = useState<(Membership & { business: Business })[]>([])
  const [loading, setLoading] = useState(!stored)
  const [membershipsLoading, setMembershipsLoading] = useState(!!stored)
  const [sessionExpired, setSessionExpired] = useState(false)
  const mountedRef = useRef(true)

  // ------------------------------------------------------------------
  // Effect 1 — Subscribe to auth state changes
  //
  // IMPORTANT: The onAuthStateChange callback must ONLY update React
  // state. Never call Supabase queries / getSession / getUser inside
  // it — doing so causes deadlocks (auth-js#762) or token-refresh
  // loops (supabase-js#2126).
  // ------------------------------------------------------------------
  useEffect(() => {
    mountedRef.current = true

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, s) => {
        if (!mountedRef.current) return
        console.log('[Auth] onAuthStateChange:', _event, s?.user?.email ?? 'no user')
        // Only set state — no async Supabase calls
        setSession(s)
        setUser(s?.user ?? null)
        setLoading(false)
        if (s?.user) {
          setSessionExpired(false)
        }
      },
    )

    return () => {
      mountedRef.current = false
      subscription.unsubscribe()
    }
  }, [])

  // ------------------------------------------------------------------
  // Effect 2 — Fetch memberships whenever `user` changes
  //
  // This is decoupled from onAuthStateChange so we don't deadlock
  // the Supabase client. On first load with an eager-restored session,
  // we use a direct REST call (Supabase client isn't initialised yet).
  // On subsequent auth events the Supabase client is ready so we use
  // the normal client.
  // ------------------------------------------------------------------
  const initialFetchDone = useRef(false)

  useEffect(() => {
    if (!user) {
      setMemberships([])
      setMembershipsLoading(false)
      initialFetchDone.current = false
      return
    }

    let cancelled = false
    setMembershipsLoading(true)

    async function load() {
      let m: (Membership & { business: Business })[]

      if (!initialFetchDone.current && stored?.session?.access_token) {
        // First fetch — Supabase client may not be initialised yet.
        // Use direct REST with the stored access token.
        console.log('[Auth] Fetching memberships (REST)...')
        m = await fetchMembershipsREST(user!.id, stored.session.access_token)
      } else {
        // Supabase client is authenticated — use normal client
        console.log('[Auth] Fetching memberships (client)...')
        m = await fetchMemberships(user!.id)
      }

      initialFetchDone.current = true

      if (!cancelled) {
        console.log('[Auth] Memberships loaded:', m.length)
        setMemberships(m)
        setMembershipsLoading(false)
      }
    }

    load()
    return () => { cancelled = true }
  // `stored` is intentionally omitted — it's only used for the first fetch
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  // ------------------------------------------------------------------
  // Effect 3 — Safety timeout for initial auth
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!loading && !membershipsLoading) return // already resolved

    const hasStoredSession = !!localStorage.getItem(STORAGE_KEY)

    const timeout = setTimeout(() => {
      if (!stored && hasStoredSession) {
        console.warn('[Auth] Auth timed out')
        setSessionExpired(true)
      }
      setLoading(false)
      setMembershipsLoading(false)
    }, 10000)

    return () => clearTimeout(timeout)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ------------------------------------------------------------------
  // Actions
  // ------------------------------------------------------------------

  async function signIn(email: string, password: string) {
    setSessionExpired(false)
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) return { error: error.message }
    return { error: null }
  }

  async function signOut() {
    await supabase.auth.signOut()
    setMemberships([])
    setSessionExpired(false)
  }

  async function refreshMemberships() {
    if (user) {
      const m = await fetchMemberships(user.id)
      setMemberships(m)
    }
  }

  return (
    <AuthContext.Provider
      value={{
        session,
        user,
        memberships,
        loading,
        membershipsLoading,
        sessionExpired,
        signIn,
        signOut,
        refreshMemberships,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
